import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import adminAxios from "./adminAxios"; 
import Swal from 'sweetalert2';
import AdminSidebar from "./AdminSidebar";
import { 
  Cpu, 
  ShieldCheck, 
  Plus, 
  Search, 
  Trash2, 
  Edit3, 
  UploadCloud, 
  TrendingUp, 
  Layers,
  X,
  Database
} from "lucide-react";

/**
 * PREMIUM CONFIGURATION
 */
const COLORS = {
  cyan: "#03f8d7",
  rose: "#ff2d75",
  slate: "#0f172a",
  surface: "rgba(15, 23, 42, 0.95)",
  border: "rgba(3, 248, 215, 0.15)",
  textMain: "#f8fafc",
  textMuted: "#94a3b8"
};

const formatImageUrl = (path) => {
  if (!path) return "https://via.placeholder.com/150";
  if (path.startsWith("blob:") || path.startsWith("http")) return path;
  return path; 
};

function AssignProducts() {
  const fileInputRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]); 
  const [editingId, setEditingId] = useState(null); 
  const [existingImages, setExistingImages] = useState([]); 
  const [newFiles, setNewFiles] = useState([]); 

  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [productSearchTerm, setProductSearchTerm] = useState("");

  // Retrieve isolated tenant context identifier safely across storage schemas
const adminSession = JSON.parse(localStorage.getItem("adminUser") || "{}");
const userSession = JSON.parse(localStorage.getItem("user") || "{}");
const tenantId = adminSession.adminId || userSession.adminId || localStorage.getItem("adminId") || "";

  const [form, setForm] = useState({
    name: "",
    price: "",
    commissionYield: "", 
    rating: 5.0,
    volumeThroughput: "0", 
    description: "",
  });

  const [assign, setAssign] = useState({
    userId: "",
    productId: "",
  });

  const fetchProducts = useCallback(async () => {
    try {
      const res = await adminAxios.get("/api/products", { params: { tenantId } });
      const data = Array.isArray(res.data) ? res.data : (res.data.products || []);
      setProducts(data);
    } catch (err) {
      console.error("Inventory synchronization failure:", err);
      setProducts([]);
    }
  }, [tenantId]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await adminAxios.get("/api/admin/users", { params: { tenantId } });
      let userData = [];
      if (Array.isArray(res.data)) {
        userData = res.data;
      } else if (res.data && Array.isArray(res.data.users)) {
        userData = res.data.users;
      } else {
        userData = Object.values(res.data || {}).find(val => Array.isArray(val)) || [];
      }
      setUsers(userData);
    } catch (err) {
      console.error("User directory sync failure:", err);
      setUsers([]);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchProducts();
    fetchUsers();
  }, [fetchProducts, fetchUsers]);

  const filteredUsers = useMemo(() => {
    return users.filter(u => 
      u.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      u.name?.toLowerCase().includes(userSearchTerm.toLowerCase())
    );
  }, [users, userSearchTerm]);

  const filteredProducts = useMemo(() => {
    return products.filter(p => 
      p.name?.toLowerCase().includes(productSearchTerm.toLowerCase())
    );
  }, [products, productSearchTerm]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setNewFiles(prev => [...prev, ...files]);
      const newPreviews = files.map(file => ({
        url: URL.createObjectURL(file),
        isBlob: true,
        originalFile: file
      }));
      setImagePreviews(prev => [...prev, ...newPreviews]);
    }
    e.target.value = "";
  };

  const removeImage = (index) => {
    const target = imagePreviews[index];
    if (target.isBlob) {
      URL.revokeObjectURL(target.url);
      setNewFiles(prev => prev.filter(f => f !== target.originalFile));
    } else {
      const filename = target.originalName;
      setExistingImages(prev => prev.filter(img => img !== filename));
    }
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const resetForm = () => {
    setForm({ name: "", price: "", commissionYield: "", rating: 5, volumeThroughput: "0", description: "" });
    setExistingImages([]);
    setNewFiles([]);
    imagePreviews.forEach(p => { if(p.isBlob) URL.revokeObjectURL(p.url) });
    setImagePreviews([]);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    
    // Append standard fields & tenant scope
    Object.keys(form).forEach(key => formData.append(key, form[key]));
    formData.append("tenantId", tenantId);
    
    // Sync with backend: remainingImages + bannerFile
    formData.append("remainingImages", JSON.stringify(existingImages));
    newFiles.forEach((file) => formData.append("bannerFile", file));

    try {
      if (editingId) {
        // FIX: Stringify the ID explicitly to prevent [object Object] or function string conversion
        const targetId = String(editingId).trim();
        await adminAxios.put(`/api/products/${targetId}`, formData);
      } else {
        await adminAxios.post("/api/products", formData);
      }
      
      Swal.fire({
        toast: true,
        position: 'top-right',
        icon: 'success',
        title: 'Registry Updated',
        showConfirmButton: false,
        timer: 2000,
        background: COLORS.slate,
        color: COLORS.cyan
      });
      
      resetForm();
      fetchProducts();
    } catch (err) {
      console.error("Submission Error:", err.response?.data || err.message);
      // More descriptive error handling
      const errorMsg = err.response?.status === 404 
        ? "Endpoint not found. Check server route configuration." 
        : "System synchronization error. Check connection to Koyeb.";
      
      Swal.fire("Update Failed", errorMsg, "error");
    }
  };

  const startEdit = (p) => {
    // Ensure we only store the string ID
    const cleanId = p._id ? String(p._id) : null;
    setEditingId(cleanId);
    
    setForm({
      name: p.name || "",
      price: p.price || "",
      commissionYield: p.commissionYield || p.profit || "", 
      rating: p.rating || 5,
      volumeThroughput: p.volumeThroughput || p.unitsSold || "0",
      description: p.specifications || p.description || "",
    });

    const serverImages = p.gallery && p.gallery.length > 0 ? p.gallery : (p.image ? [p.image] : []);
    setExistingImages(serverImages);
    setImagePreviews(serverImages.map(img => ({
      url: formatImageUrl(img),
      isBlob: false,
      originalName: img 
    })));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteProduct = async (id) => {
    const confirm = await Swal.fire({
      title: "Confirm Purge?",
      text: "Asset will be decommissioned permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: COLORS.rose,
      confirmButtonText: "Purge",
      background: COLORS.slate,
      color: "#fff"
    });

    if (confirm.isConfirmed) {
      try {
        const targetId = String(id).trim();
        await adminAxios.delete(`/api/products/${targetId}`, { data: { tenantId } });
        fetchProducts();
      } catch (err) {
        Swal.fire("Error", "Decommission failed.", "error");
      }
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!assign.userId || !assign.productId) return;
    
    const prod = products.find(p => String(p._id) === String(assign.productId));
    try {
      await adminAxios.post("/api/orders/assign", {
        userId: assign.userId,
        productId: assign.productId,
        profit: prod?.commissionYield || prod?.profit || 0,
        price: prod?.price || 0,
        tenantId
      });

      Swal.fire({ 
        toast: true, 
        position: 'top-right', 
        title: 'Deployment Successful', 
        background: COLORS.cyan, 
        color: COLORS.slate, 
        showConfirmButton: false, 
        timer: 2000 
      });
      setAssign({ userId: "", productId: "" });
      setUserSearchTerm("");
      setProductSearchTerm("");
    } catch (err) {
      Swal.fire("Allocation Failed", "Unauthorized or node busy.", "error");
    }
  };

  return (
    <div style={styles.container} data-tenant-scope={tenantId}>
      <AdminSidebar />
      <div style={styles.mainContent}>
        <div style={styles.topBar}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Cpu color={COLORS.cyan} size={20} />
              <h1 style={styles.topBarTitle}>ASSET DEPLOYMENT CONTROL</h1>
            </div>
            <p style={styles.topBarSub}>CONFIGURE HARDWARE NODES & LOGISTICS FLOW </p>
          </div>
          <div style={styles.liveStatus}>
             <span style={styles.statusDot}></span>
             <span style={styles.statusText}>TERMINAL ACTIVE</span>
          </div>
        </div>

        <div style={styles.contentPadding}>
          <div style={styles.gridContainer}>
            
            {/* COMPONENT: ASSET EDITOR */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <Plus size={16} color={COLORS.cyan} />
                <h2 style={styles.cardTitle}>{editingId ? "MODIFY DEPLOYMENT" : "INITIALIZE NEW ASSET"}</h2>
              </div>
              <form onSubmit={handleSubmit} style={styles.formStack}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>MEDIA DOCUMENTATION</label>
                  <div style={styles.uploadZone} onClick={() => fileInputRef.current.click()}>
                    <input 
                      type="file" 
                      multiple 
                      ref={fileInputRef}
                      onChange={handleImageChange} 
                      style={{ display: 'none' }} 
                    />
                    <UploadCloud size={24} color={COLORS.cyan} />
                    <span style={styles.uploadLabel}>UPLOAD ASSET VISUALS</span>
                  </div>

                  <div style={styles.previewContainer}>
                    {imagePreviews.map((item, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <img src={item.url} alt="Preview" style={styles.previewImg} />
                        <button type="button" onClick={() => removeImage(index)} style={styles.removeImgBtn}><X size={10} /></button>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>ASSET DESIGNATION</label>
                  <input name="name" value={form.name} onChange={handleChange} required style={styles.input} placeholder="e.g. Quantum Node X1" />
                </div>

                <div style={styles.row}>
                  <div style={styles.flexInput}>
                    <label style={styles.label}>BASE VALUATION ($)</label>
                    <input name="price" type="number" value={form.price} onChange={handleChange} required style={styles.input} />
                  </div>
                  <div style={styles.flexInput}>
                    <label style={styles.label}>PROJECTED YIELD ($)</label>
                    <input name="commissionYield" type="number" value={form.commissionYield} onChange={handleChange} required style={styles.input} />
                  </div>
                </div>

                <div style={styles.row}>
                  <div style={styles.flexInput}>
                    <label style={styles.label}>RELIABILITY INDEX</label>
                    <input name="rating" type="number" step="0.1" value={form.rating} onChange={handleChange} style={styles.input} />
                  </div>
                  <div style={styles.flexInput}>
                    <label style={styles.label}>THROUGHPUT CYCLES</label>
                    <input name="volumeThroughput" type="text" value={form.volumeThroughput} onChange={handleChange} style={styles.input} />
                  </div>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>TECHNICAL SPECIFICATIONS</label>
                  <textarea name="description" value={form.description} onChange={handleChange} style={{ ...styles.input, minHeight: "80px", resize: "none" }} />
                </div>

                <div style={styles.buttonContainer}>
                  <button type="submit" style={styles.btnPrimary}>{editingId ? "APPLY MODS" : "DEPLOY ASSET"}</button>
                  {editingId && <button type="button" onClick={resetForm} style={styles.btnSecondary}>ABORT</button>}
                </div>
              </form>
            </div>

            {/* COMPONENT: ALLOCATION ENGINE */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <Layers size={16} color={COLORS.cyan} />
                <h2 style={styles.cardTitle}>NODE ALLOCATION</h2>
              </div>
              <form onSubmit={handleAssign} style={styles.formStack}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>TARGET NODE (USER)</label>
                  <div style={styles.searchSelectContainer}>
                    <div style={styles.searchWrapper}>
                      <Search size={14} color={COLORS.cyan} />
                      <input 
                        type="text" 
                        placeholder="SCAN EMAIL..." 
                        value={userSearchTerm} 
                        onChange={(e) => setUserSearchTerm(e.target.value)} 
                        style={styles.searchOverlayInput} 
                      />
                    </div>
                    <select 
                      value={assign.userId} 
                      onChange={(e) => setAssign({...assign, userId: e.target.value})} 
                      required 
                      style={styles.selectWithSearch}
                    >
                      <option value="">{filteredUsers.length} NODES IDENTIFIED</option>
                      {filteredUsers.map((u) => (
                        <option key={String(u._id)} value={String(u._id)}>{u.email}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>OPERATIONAL HARDWARE</label>
                  <div style={styles.searchSelectContainer}>
                    <div style={styles.searchWrapper}>
                      <Search size={14} color={COLORS.cyan} />
                      <input 
                        type="text" 
                        placeholder="SCAN ASSET..." 
                        value={productSearchTerm} 
                        onChange={(e) => setProductSearchTerm(e.target.value)} 
                        style={styles.searchOverlayInput} 
                      />
                    </div>
                    <select 
                      value={assign.productId} 
                      onChange={(e) => setAssign({...assign, productId: e.target.value})} 
                      required 
                      style={styles.selectWithSearch}
                    >
                      <option value="">{filteredProducts.length} ASSETS READY</option>
                      {filteredProducts.map((p) => (
                        <option key={String(p._id)} value={String(p._id)}>{p.name} (${p.price})</option>
                      ))}
                    </select>
                  </div>
                </div>

                <button type="submit" style={styles.btnSuccess}>CONFIRM ALLOCATION</button>
              </form>

              <div style={styles.allocationFooter}>
                <ShieldCheck size={14} color={COLORS.cyan} />
                <span>SECURE END-TO-END DEPLOYMENT ENABLED</span>
              </div>
            </div>
          </div>

          {/* COMPONENT: REGISTRY TABLE */}
          <div style={{ ...styles.card, marginTop: "25px", padding: 0 }}>
            <div style={styles.tableHeader}>
              <Database size={16} color={COLORS.cyan} />
              <h2 style={{ ...styles.cardTitle, margin: 0, borderLeft: 'none' }}>GLOBAL ASSET REGISTRY</h2>
            </div>
            <div style={styles.tableWrapper}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeadRow}>
                      <th style={styles.th}>VISUAL</th>
                      <th style={styles.th}>DESIGNATION</th>
                      <th style={styles.th}>VALUATION</th>
                      <th style={styles.th}>METRICS</th> 
                      <th style={styles.th}>OPERATIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((p) => (
                      <tr key={String(p._id)} style={styles.trHover}>
                        <td style={styles.td}>
                          <img src={formatImageUrl(p.image)} alt="asset" style={styles.tableImg} />
                        </td>
                        <td style={{ ...styles.td, fontWeight: '800', color: '#fff' }}>{p.name}</td>
                        <td style={{ ...styles.td, color: COLORS.cyan, fontFamily: 'monospace' }}>${Number(p.price).toLocaleString()}</td>
                        <td style={styles.td}>
                          <div style={{ color: COLORS.cyan, fontWeight: "800", fontSize: "10px", display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <TrendingUp size={10} /> YIELD: ${p.commissionYield || 0}
                          </div>
                          <div style={{ color: COLORS.textMuted, fontSize: "10px", marginTop: '4px' }}>RI: {p.rating || 5.0}</div>
                        </td>
                        <td style={styles.td}>
                          <div style={styles.actionFlex}>
                            <button onClick={() => startEdit(p)} style={styles.actionBtnEdit}><Edit3 size={14} /></button>
                            <button onClick={() => handleDeleteProduct(p._id)} style={styles.actionBtnDelete}><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.3; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: { display: "flex", minHeight: "100vh", backgroundColor: "#060608", fontFamily: "'Inter', sans-serif", color: COLORS.textMain },
  mainContent: { flex: 1, paddingLeft: '280px' },
  topBar: { background: COLORS.surface, backdropFilter: "blur(10px)", padding: "20px 40px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${COLORS.border}`, position: 'sticky', top: 0, zIndex: 10 },
  topBarTitle: { margin: 0, fontSize: "18px", fontWeight: "900", letterSpacing: "2px" },
  topBarSub: { margin: "4px 0 0 0", fontSize: "9px", color: COLORS.cyan, fontWeight: "800", letterSpacing: "1px" },
  liveStatus: { display: "flex", alignItems: "center", gap: "10px", background: "rgba(0,0,0,0.3)", padding: "8px 15px", borderRadius: "8px", border: `1px solid ${COLORS.border}` },
  statusDot: { height: 8, width: 8, backgroundColor: COLORS.cyan, borderRadius: "50%", animation: "pulse 2s infinite" },
  statusText: { fontSize: "9px", fontWeight: "900", color: COLORS.cyan, letterSpacing: "1px" },
  contentPadding: { padding: "40px" },
  gridContainer: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: "25px" },
  card: { background: COLORS.surface, padding: "25px", borderRadius: "15px", border: `1px solid ${COLORS.border}`, boxShadow: "0 10px 30px rgba(0,0,0,0.5)" },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' },
  cardTitle: { fontSize: "12px", fontWeight: "900", color: "#fff", letterSpacing: "1px" },
  formStack: { display: "flex", flexDirection: "column", gap: "18px" },
  inputGroup: { display: "flex", flexDirection: "column", gap: "8px" },
  row: { display: "flex", gap: "15px" },
  flexInput: { flex: 1 },
  label: { fontSize: "10px", fontWeight: "800", color: COLORS.textMuted, letterSpacing: "0.5px" },
  input: { width: '100%', padding: "12px 15px", borderRadius: "8px", background: "rgba(0,0,0,0.2)", border: `1px solid ${COLORS.border}`, fontSize: "13px", color: "#fff", outline: "none", boxSizing: 'border-box' },
  searchSelectContainer: { display: 'flex', flexDirection: 'column', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${COLORS.border}` },
  searchWrapper: { display: 'flex', alignItems: 'center', gap: '10px', padding: '0 15px', background: 'rgba(255,255,255,0.03)', borderBottom: `1px solid ${COLORS.border}` },
  searchOverlayInput: { flex: 1, padding: '12px 0', background: 'transparent', border: 'none', fontSize: '12px', color: COLORS.cyan, outline: 'none' },
  selectWithSearch: { width: '100%', padding: '12px', background: '#000', border: 'none', fontSize: '12px', color: '#fff', cursor: 'pointer', outline: 'none' },
  uploadZone: { minHeight: '80px', background: 'rgba(3, 248, 215, 0.02)', borderRadius: '10px', border: `1px dashed ${COLORS.cyan}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', gap: '8px' },
  uploadLabel: { fontSize: '9px', color: COLORS.cyan, fontWeight: "900", letterSpacing: '1px' },
  previewContainer: { display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "10px" },
  previewImg: { width: "50px", height: "50px", objectFit: "cover", borderRadius: "6px", border: `1px solid ${COLORS.border}` },
  removeImgBtn: { position: 'absolute', top: '-5px', right: '-5px', background: COLORS.rose, color: 'white', border: 'none', borderRadius: '50%', width: '16px', height: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  buttonContainer: { display: "flex", gap: "12px", marginTop: "10px" },
  btnPrimary: { flex: 1, padding: "14px", background: COLORS.cyan, color: COLORS.slate, border: "none", borderRadius: "8px", fontWeight: "900", cursor: "pointer", fontSize: '11px', letterSpacing: '1px' },
  btnSecondary: { padding: "14px 25px", background: "transparent", color: COLORS.textMuted, border: `1px solid ${COLORS.textMuted}`, borderRadius: "8px", fontWeight: "900", cursor: "pointer", fontSize: '11px' },
  btnSuccess: { padding: "14px", background: "transparent", color: COLORS.cyan, border: `1px solid ${COLORS.cyan}`, borderRadius: "8px", fontWeight: "900", cursor: "pointer", fontSize: '11px', letterSpacing: '1px' },
  allocationFooter: { marginTop: '20px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '9px', color: COLORS.textMuted, fontWeight: '700' },
  tableHeader: { padding: '20px', background: 'rgba(255,255,255,0.02)', borderBottom: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', gap: '10px' },
  tableWrapper: { overflowX: 'auto' },
  table: { width: "100%", borderCollapse: "collapse" },
  tableHeadRow: { background: "rgba(0,0,0,0.3)" },
  th: { padding: "15px 20px", borderBottom: `1px solid ${COLORS.border}`, textAlign: "left", fontSize: "10px", color: COLORS.textMuted, fontWeight: "900", letterSpacing: "1px" },
  td: { padding: "15px 20px", borderBottom: `1px solid ${COLORS.border}`, fontSize: "12px" },
  tableImg: { width: "45px", height: "45px", borderRadius: "8px", objectFit: "cover", border: `1px solid ${COLORS.border}` },
  actionFlex: { display: 'flex', gap: '10px' },
  actionBtnEdit: { width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(3, 248, 215, 0.1)', color: COLORS.cyan, border: 'none', borderRadius: '6px', cursor: 'pointer' },
  actionBtnDelete: { width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255, 45, 117, 0.1)', color: COLORS.rose, border: 'none', borderRadius: '6px', cursor: 'pointer' }
};

export default AssignProducts;